// Production Stage Digest — shared config: column/stage definitions, the
// premium per-stage colour palette, and the client-side pivot helpers.
//
// Counting model is MULTI-BUCKET: an order appears in every column where that
// stage is its current in-progress step. The per-entity Total is a DISTINCT
// count of the entity's pending orders (so columns can sum to more than Total).

// Leaf column keys in display order.
export const COLUMN_KEYS = [
  'internal_cad_not_allotted', 'internal_cad_wip',
  'external_cad',
  'ghat_no_requirement', 'ghat_pending_gold_order', 'ghat_waiting_gold', 'ghat_confirmation',
  'rm_diamonds_pending', 'rm_diamonds_order_raised', 'rm_diamonds_collected',
  'rm_color_stones_pending', 'rm_color_stones_order_raised', 'rm_color_stones_collected',
  'rm_pearls_pending', 'rm_pearls_order_raised', 'rm_pearls_collected',
  'rm_none',
  'setting', 'qc', 'delivery',
];

// Grouped structure powering the two-row table header + the tiles.
export const STAGE_GROUPS = [
  {
    key: 'internal_cad', label: 'Internal CAD', cols: [
      { key: 'internal_cad_not_allotted', short: 'Not Allotted' },
      { key: 'internal_cad_wip', short: 'WIP' },
    ],
  },
  { key: 'external_cad', label: 'External CAD', cols: [{ key: 'external_cad', short: 'External CAD' }] },
  {
    key: 'ghat', label: 'Ghat', cols: [
      { key: 'ghat_no_requirement', short: 'No Requirement' },
      { key: 'ghat_pending_gold_order', short: 'Pending Gold Order' },
      { key: 'ghat_waiting_gold', short: 'Waiting for Gold' },
      { key: 'ghat_confirmation', short: 'Ghat Confirmation' },
    ],
  },
  {
    key: 'raw_material', label: 'Raw Material', cols: [
      { key: 'rm_diamonds_pending', short: 'Dia · Pending' },
      { key: 'rm_diamonds_order_raised', short: 'Dia · Ordered' },
      { key: 'rm_diamonds_collected', short: 'Dia · Collected' },
      { key: 'rm_color_stones_pending', short: 'CS · Pending' },
      { key: 'rm_color_stones_order_raised', short: 'CS · Ordered' },
      { key: 'rm_color_stones_collected', short: 'CS · Collected' },
      { key: 'rm_pearls_pending', short: 'Pearl · Pending' },
      { key: 'rm_pearls_order_raised', short: 'Pearl · Ordered' },
      { key: 'rm_pearls_collected', short: 'Pearl · Collected' },
      { key: 'rm_none', short: 'No Requirement' },
    ],
  },
  { key: 'setting', label: 'Setting', cols: [{ key: 'setting', short: 'Setting' }] },
  { key: 'qc', label: 'QC', cols: [{ key: 'qc', short: 'QC' }] },
  { key: 'delivery', label: 'Deliver', cols: [{ key: 'delivery', short: 'Deliver' }] },
];

// leaf column key -> owning group key (for colour + border lookups).
export const COLUMN_GROUP = STAGE_GROUPS.reduce((acc, g) => {
  g.cols.forEach((c) => { acc[c.key] = g.key; });
  return acc;
}, {});

// The first leaf column of each group starts a BOLD divider; the rest of a
// group's sub-columns get a THIN divider.
export const GROUP_FIRST_COLS = new Set(STAGE_GROUPS.map((g) => g.cols[0].key));

// Wave 4.10f — Within the Raw Material group, start each side-material
// sub-block (Diamonds / Colour Stones / Pearls / No Requirement) with a
// distinct COLOURED 2px divider so the three-column material blocks read as
// separate units. Diamonds reuses the group's sky bar (it is also the group
// boundary); the others get their own hue. Literal class strings so the
// Tailwind JIT keeps them in the build.
export const RM_SUBGROUP_FIRST_COLS = {
  rm_diamonds_pending: 'border-sky-400',
  rm_color_stones_pending: 'border-violet-400',
  rm_pearls_pending: 'border-rose-400',
  rm_none: 'border-slate-400',
};

// Premium per-stage palette. Literal Tailwind class strings so the JIT keeps
// them in the build. ``head`` = header fill, ``headBar`` = bold group border,
// ``tile`` = tile border+text, ``ring`` = hover/focus, ``count`` = count text.
export const STAGE_STYLE = {
  internal_cad: { head: 'bg-violet-100 text-violet-900', headBar: 'border-violet-400', tile: 'border-violet-300 text-violet-700', ring: 'hover:border-violet-500 focus-visible:ring-violet-400', dot: 'bg-violet-500', count: 'text-violet-700', icon: 'text-violet-600' },
  external_cad: { head: 'bg-fuchsia-100 text-fuchsia-900', headBar: 'border-fuchsia-400', tile: 'border-fuchsia-300 text-fuchsia-700', ring: 'hover:border-fuchsia-500 focus-visible:ring-fuchsia-400', dot: 'bg-fuchsia-500', count: 'text-fuchsia-700', icon: 'text-fuchsia-600' },
  ghat: { head: 'bg-amber-100 text-amber-900', headBar: 'border-amber-400', tile: 'border-amber-300 text-amber-700', ring: 'hover:border-amber-500 focus-visible:ring-amber-400', dot: 'bg-amber-500', count: 'text-amber-700', icon: 'text-amber-600' },
  raw_material: { head: 'bg-sky-100 text-sky-900', headBar: 'border-sky-400', tile: 'border-sky-300 text-sky-700', ring: 'hover:border-sky-500 focus-visible:ring-sky-400', dot: 'bg-sky-500', count: 'text-sky-700', icon: 'text-sky-600' },
  setting: { head: 'bg-teal-100 text-teal-900', headBar: 'border-teal-400', tile: 'border-teal-300 text-teal-700', ring: 'hover:border-teal-500 focus-visible:ring-teal-400', dot: 'bg-teal-500', count: 'text-teal-700', icon: 'text-teal-600' },
  qc: { head: 'bg-cyan-100 text-cyan-900', headBar: 'border-cyan-400', tile: 'border-cyan-300 text-cyan-700', ring: 'hover:border-cyan-500 focus-visible:ring-cyan-400', dot: 'bg-cyan-500', count: 'text-cyan-700', icon: 'text-cyan-600' },
  delivery: { head: 'bg-emerald-100 text-emerald-900', headBar: 'border-emerald-400', tile: 'border-emerald-300 text-emerald-700', ring: 'hover:border-emerald-500 focus-visible:ring-emerald-400', dot: 'bg-emerald-500', count: 'text-emerald-700', icon: 'text-emerald-600' },
  total: { head: 'bg-[var(--ink-navy)] text-white', headBar: 'border-[var(--gold-accent)]', tile: 'border-[var(--gold-accent)] text-[var(--gold-accent)]', ring: 'hover:border-[var(--gold-accent)] focus-visible:ring-[var(--gold-accent)]', dot: 'bg-[var(--gold-accent)]', count: 'text-[var(--ink-navy)]', icon: 'text-[var(--gold-accent)]' },
};

const PAGE_OF = (o) => (o?.reference_no !== null && o?.reference_no !== undefined && o?.reference_no !== '' ? String(o.reference_no) : null);

// Aggregate a list of orders into {count, pages, orders} where ``count`` is the
// number of order forms and ``pages`` is the number of DISTINCT page numbers.
function aggList(list) {
  const pageSet = new Set();
  for (const o of list) { const p = PAGE_OF(o); if (p !== null) pageSet.add(p); }
  return { count: list.length, pages: pageSet.size, orders: list };
}

// Per-entity aggregate: the distinct Total + one {count,pages,orders} per column.
function aggregateEntity(orders) {
  const buckets = {};
  COLUMN_KEYS.forEach((c) => { buckets[c] = []; });
  for (const o of orders) {
    for (const c of (o.columns || [])) if (buckets[c]) buckets[c].push(o);
  }
  const cells = {};
  COLUMN_KEYS.forEach((c) => { cells[c] = aggList(buckets[c]); });
  return { total: aggList(orders), cells };
}

const byTotalDesc = (a, b) => (b.total.count - a.total.count) || a.label.localeCompare(b.label);

// Build the pivot rows for the chosen primary dimension. Each row carries its
// reverse-dimension ``children`` (collapsed by default in the UI).
export function buildPivot(orders, primaryDim) {
  const isCust = primaryDim === 'customer';
  const pId = isCust ? 'customer_id' : 'goldsmith_id';
  const pName = isCust ? 'customer_name' : 'goldsmith_name';
  const sId = isCust ? 'goldsmith_id' : 'customer_id';
  const sName = isCust ? 'goldsmith_name' : 'customer_name';

  const rowsMap = new Map();
  for (const o of orders) {
    const rk = o[pId] || `name:${o[pName] || 'Unassigned'}`;
    let row = rowsMap.get(rk);
    if (!row) { row = { key: rk, label: o[pName] || 'Unassigned', orders: [], childMap: new Map() }; rowsMap.set(rk, row); }
    row.orders.push(o);
    const ck = o[sId] || `name:${o[sName] || 'Unassigned'}`;
    let ch = row.childMap.get(ck);
    if (!ch) { ch = { key: ck, label: o[sName] || 'Unassigned', orders: [] }; row.childMap.set(ck, ch); }
    ch.orders.push(o);
  }

  return Array.from(rowsMap.values()).map((r) => ({
    key: r.key,
    label: r.label,
    ...aggregateEntity(r.orders),
    children: Array.from(r.childMap.values())
      .map((c) => ({ key: c.key, label: c.label, ...aggregateEntity(c.orders) }))
      .sort(byTotalDesc),
  })).sort(byTotalDesc);
}

// Group-level distinct tiles for the header strip (one per production stage).
export function buildTiles(orders) {
  return STAGE_GROUPS.map((g) => {
    const colSet = new Set(g.cols.map((c) => c.key));
    const seen = new Set();
    const list = [];
    for (const o of orders) {
      if ((o.columns || []).some((c) => colSet.has(c)) && !seen.has(o.id)) {
        seen.add(o.id); list.push(o);
      }
    }
    return { key: g.key, label: g.label, ...aggList(list) };
  });
}
